import React, { useState, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useForm } from 'react-hook-form'

import { AlertBox, Icons, Button, Field, Spinner } from 'msp-components'
import {
  convertDateToISODate,
  convertISODateToGermanDate,
  useAccount,
  IMachine
} from 'msp-integrations'

import getUpdateMachineFields from './AccountMachineUpdate.fields'
import translations from './AccountMachineUpdate.constants'
import * as S from './AccountMachineUpdate.styles'

export type AccountMachineUpdateProps = {
  machine: IMachine
}

const AccountMachineUpdate = ({ machine }: AccountMachineUpdateProps) => {
  const updateBoxRef = useRef<AlertBox>()
  const { formatMessage } = useIntl()
  const [errorMessage, setErrorMessage] = useState(null)
  const fieldConfig = getUpdateMachineFields()
  const {
    machines: { isProcessingAction, updateMachine }
  } = useAccount()
  const form = useForm({
    defaultValues: {
      SerialNo: machine.SerialNo,
      purchaseDate: convertISODateToGermanDate(machine.purchaseDate)
    }
  })

  const resetForm = () => {
    form.clearErrors()
    form.reset({
      SerialNo: '',
      purchaseDate: ''
    })
  }

  const cancelUpdate = async () => {
    resetForm()
    setErrorMessage(null)
    updateBoxRef?.current.closeAlert()
  }

  const submitForm = async (data: any) => {
    try {
      form.clearErrors()

      const payload = {
        SerialNo: machine.SerialNo,
        purchaseDate: convertDateToISODate(data.purchaseDate)
      }

      setErrorMessage(null)
      await updateMachine({ body: payload })
        .then((res) => res)
        .catch((error) => {
          setErrorMessage(error?.message || 'There was a technical issue')
        })
    } catch (error) {
      form.setError('global', { type: 'manual', message: error.message })
    }
  }

  return (
    <>
      <div
        className={`${S.ButtonWrapper} ${S.ButtonUnderline}`}
        onClick={() => !isProcessingAction && updateBoxRef?.current.showAlert()}
      >
        {formatMessage(translations.updateMachine)}
      </div>
      <AlertBox
        type='standard'
        ref={updateBoxRef}
        extendedWrapperStyle={S.AlertBoxWrapper}
        headlineColor={S.ConfirmationHeadline}
        title={formatMessage(translations.updateMachineTitle)}
        closeButtonIcon={<Icons iconName='Close' size='base' />}
      >
        {errorMessage && (
          <div className={S.ErrorMessageWrapper}>{errorMessage}</div>
        )}
        {form.formState?.isSubmitting && <Spinner fullScreen />}

        <form
          id={'machine-update'}
          onSubmit={form.handleSubmit(submitForm)}
          autoComplete='off'
        >
          <div className={S.FormFieldsWrapper}>
            <Field
              control={form.control}
              config={fieldConfig.SerialNo}
              error={form.formState.errors?.SerialNo}
              readOnly={true}
              isDisabled={true}
            />
            <Field
              className={S.FormFieldItem}
              control={form.control}
              config={fieldConfig.purchaseDate}
              error={form.formState.errors?.purchaseDate}
              isDisabled={form.formState.isSubmitting}
            />
          </div>
          <div className={S.ConfirmationButtonsWrapper}>
            <Button
              type={'secondary'}
              variant='outline'
              className={S.ConfirmationCancelButton}
              isDisabled={form.formState?.isSubmitting}
              onClick={cancelUpdate}
            >
              {formatMessage(translations.updateMachineCancel)}
            </Button>
            <Button
              isSubmit
              type='secondary'
              isDisabled={form.formState?.isSubmitting}
            >
              {formatMessage(translations.updateMachineConfirm)}
            </Button>
          </div>
        </form>
      </AlertBox>
    </>
  )
}

export default AccountMachineUpdate
